/** One group, one standard row, one student — simplified UI (no accordion, no lists). */
export const isSoloSingleStudentGrouping = (groups) => {
  if (!Array.isArray(groups) || groups.length !== 1) return false
  const standardGroups = groups[0]?.standardGroups || []
  if (standardGroups.length !== 1) return false
  const students = standardGroups[0]?.students || []
  return students.length === 1
}

/**
 * Groups saved to Redux for Group & Assign should only include rows with standards to show.
 */
export const filterGroupsWithStandardGroups = (groups = []) =>
  (Array.isArray(groups) ? groups : []).filter(
    (g) => Array.isArray(g?.standardGroups) && g.standardGroups.length > 0
  )

/** Stable id for recommendation API payloads and lookup (tests + decks). */
export const getGroupIdForRecommendations = (group) => group?.name || ''

/** Band fields live on each standard row (subgroup). */
export const getStandardGroupThreshold = (standardGroup) =>
  standardGroup?.threshold

export const getStandardGroupIsMastered = (standardGroup) =>
  !!standardGroup?.isMastered

/**
 * Pear Deck vs test recommendations: bucket each standard row (subgroup), not the whole group.
 *
 * When `showTestRecommendationsOnly` is set, deck recommendations are skipped entirely and every
 * band (including the lowest) is routed to the test recommendation API instead. This is used for
 * districts that never create a Pear Deck account (e.g. GCPS).
 */
export const buildRecommendationGroupLists = (
  currentGroups,
  { showTestRecommendationsOnly = false } = {}
) => {
  const searchGroups = []
  const searchGroupsForLowestBand = []
  const searchGroupsForMasteredBand = []

  for (const group of currentGroups || []) {
    const groupId = getGroupIdForRecommendations(group)
    const standardGroups = group.standardGroups || []
    for (const standardGroup of standardGroups) {
      const standardId = standardGroup._id
      if (!standardId || !groupId) continue

      const threshold = getStandardGroupThreshold(standardGroup)
      const isMastered = getStandardGroupIsMastered(standardGroup)

      const entry = {
        groupId: `${groupId}_${standardId}`,
        standardIds: [standardId],
        masteryPercentage: standardGroup.scorePercentage ?? 0,
      }

      let collection = searchGroups
      if (isMastered) {
        collection = searchGroupsForMasteredBand
      } else if (!threshold) {
        collection = searchGroupsForLowestBand
      }
      collection.push(entry)
    }
  }

  if (showTestRecommendationsOnly) {
    return {
      testsGroups: [
        ...searchGroupsForLowestBand,
        ...searchGroups,
        ...searchGroupsForMasteredBand,
      ],
      decksGroups: [],
    }
  }

  return {
    testsGroups: [...searchGroups, ...searchGroupsForMasteredBand],
    decksGroups: [...searchGroupsForLowestBand, ...searchGroups],
  }
}

const mapStudentsForSegment = (students = []) =>
  students.map((student) => ({
    studentId: student._id,
    studentName: student.name || student.studentName || '',
  }))

/** Per-standard rows with students for feedback review. */
export const buildSubGroupsFeedbackPayload = (standardGroups = []) =>
  (standardGroups || []).map((standardGroup) => ({
    standardId: standardGroup._id,
    identifier: standardGroup.identifier,
    isMastered: !!standardGroup.isMastered,
    threshold: standardGroup.threshold ?? null,
    scorePercentage: standardGroup.scorePercentage ?? null,
    students: mapStudentsForSegment(standardGroup.students),
  }))

export const buildGroupAndAssignFeedbackSegmentPayload = ({
  response,
  source,
  group,
  reportContext = {},
  lcbContext = {},
}) => {
  const { name: studentGroupName, studentCount = 0, standardGroups = [] } =
    group || {}

  return {
    response,
    source,
    studentGroup: {
      name: studentGroupName,
      studentCount,
      standardsCount: standardGroups.length,
    },
    subGroups: buildSubGroupsFeedbackPayload(standardGroups),
    ...reportContext,
    ...lcbContext,
  }
}
